import { JobStatus } from '@gridcon-energy/schaltungstool-backend'
import type {
  JobClusterCreate,
  JobClusterRead,
  JobClusterRunRead,
  JobRead,
  Matrix,
} from '@gridcon-energy/schaltungstool-backend'
import exampleMatrix from '~/pages/sina/[survey]/example-matrix'
import type { JobClusterFormSchemaType } from '~/lib/schemas/jobCluster'
import { joinPathSegments, normalizePathSegment } from '~/helper/path'
import { formatDate, ZOD_STRING_DATE_FORMAT } from '~/helper/date'
import dayjs from 'dayjs/esm'

export enum JobClusterStatus {
  created = 'created',
  running = 'running',
  finished = 'finished',
  finished_with_errors = 'finished_with_errors',
}

/** /sina */
export const SinaDashboardPageQueryParams = {
  refresh: 'refresh',
  project: 'projectId',
  survey: 'surveyId',
} as const

/** /sina/[survey]/job-cluster */
export const JobClusterPageQueryParams = {
  jobCluster: 'jobClusterId',
  mode: 'mode',
} as const

/** /sina/[survey]/results */
export const ResultsPageQueryParams = {
  result: 'resultId',
} as const

export enum JobClusterMatrixEditMode {
  COPY = 'copy', // when copying from other job cluster
}

export function emptyJobCluster(survey_id: number): JobClusterCreate {
  return {
    name: '',
    survey_id: survey_id,
    matrix: exampleMatrix,
  }
}

export function isJobClusterRead(
  item: JobClusterCreate | JobClusterRead | JobClusterFormSchemaType,
): item is JobClusterRead {
  return 'id' in item && item.id !== undefined
}

/**
 * Matrix topologies keep start/end as `YYYY-MM-DD` (see `TopologyEntry` schema),
 * whereas `v-date-input` works with `Date`.
 */
export function toMatrixDate(date: Date | string | null | undefined): string | null {
  if (!date) {
    return null
  }
  return formatDate(date, ZOD_STRING_DATE_FORMAT)
}

export function fromMatrixDate(date: string | null): Date | null {
  if (!date) {
    return null
  }
  return dayjs(date).toDate()
}

export function toJobClusterForm(jobCluster: JobClusterRead): JobClusterFormSchemaType {
  return {
    id: jobCluster.id,
    survey_id: jobCluster.survey_id,
    name: jobCluster.name,
    matrix: jobCluster.matrix,
  }
}

export function getJobClusterTargetPath(jobCluster: Pick<JobClusterRead, 'target_root' | 'target_directory'>): string {
  const root = jobCluster.target_root
  const targetDirectory = normalizePathSegment(jobCluster.target_directory)

  if (!targetDirectory) {
    return root
  }

  return joinPathSegments(root, targetDirectory)
}

export function getSortedJobClusterRuns(jobCluster: JobClusterRead): JobClusterRunRead[] {
  return jobCluster.runs.toSorted((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
}

export function getLatestJobClusterRun(jobCluster: JobClusterRead): JobClusterRunRead | null {
  return jobCluster.latest_run ?? getSortedJobClusterRuns(jobCluster)[0] ?? null
}

export function hasStartedJobCluster(jobCluster: JobClusterRead): boolean {
  return getLatestJobClusterRun(jobCluster) !== null
}

export function canStartJobCluster(jobCluster: JobClusterRead): boolean {
  return !hasStartedJobCluster(jobCluster)
}

export function isJobClusterRunActive(run: JobClusterRunRead | null | undefined): boolean {
  return run?.state === JobClusterStatus.created || run?.state === JobClusterStatus.running
}

export function getJobClusterRunFailedJobs(run: JobClusterRunRead | null | undefined): JobRead[] {
  if (!run) {
    return []
  }
  return run.jobs.filter((job) => job.status === JobStatus.Failed)
}

export function getJobClusterRunJobsByStatus(run: JobClusterRunRead | null | undefined, status: JobStatus): JobRead[] {
  if (!run) {
    return []
  }
  return run.jobs.filter((job) => job.status === status)
}

export function hasFullYearTopology(matrix: Matrix): boolean {
  return matrix.topologies.some((topology) => topology.included && topology.full_year)
}

export function getJobClusterRunExportFilename(run: Pick<JobClusterRunRead, 'export_identifier'>): string {
  return `${run.export_identifier}_Export.csv`
}

export function canRestartJobCluster(jobCluster: JobClusterRead): boolean {
  const latestRun = getLatestJobClusterRun(jobCluster)
  if (!latestRun || isJobClusterRunActive(latestRun)) {
    return false
  }
  return latestRun.state === JobClusterStatus.finished_with_errors || getJobClusterRunFailedJobs(latestRun).length > 0
}

export enum SinaSectionEnum {
  JOB_CLUSTER = 'job-cluster',
  RESULTS = 'results',
  TABLE = 'table',
  TIMELINE = 'timeline',
  SWITCHING_WINDOW = 'switching_window',
}

export const SinaSectionIcons: Record<SinaSectionEnum, string> = {
  [SinaSectionEnum.JOB_CLUSTER]: 'mdi-matrix',
  [SinaSectionEnum.RESULTS]: 'mdi-file-chart',
  [SinaSectionEnum.TABLE]: 'mdi-file-table-box-multiple-outline',
  [SinaSectionEnum.TIMELINE]: 'mdi-chart-line',
  [SinaSectionEnum.SWITCHING_WINDOW]: 'mdi-calendar-expand-horizontal-outline',
}
